/**
 * Canvas layout type definitions and hardcoded layout options.
 * Layouts define how photo slots are arranged on a canvas (e.g. stacked 2, 2x2 grid).
 * Different options per paperType: collage_4x6 (grid-style) and strip (stacked).
 */

/** Position and size of a single photo slot, in percentage (0-100) of canvas */
export interface LayoutSlot {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Gap between slots as percentage of canvas (applied on all sides when rendering). */
export const SLOT_GAP_PERCENT = 2;

/** Orientation of the paper/canvas (portrait = taller than wide, landscape = wider than tall). */
export type LayoutOrientation = 'portrait' | 'landscape';

/** A canvas layout: id, display name, paper type, orientation, and slot geometry */
export interface CanvasLayout {
  id: string;
  name: string;
  paperType: 'collage_4x6' | 'strip';
  orientation: LayoutOrientation;
  slots: LayoutSlot[];
}

/** Hardcoded layouts for 4x6 collage portrait: grid-style arrangements */
export const COLLAGE_4X6_LAYOUTS: CanvasLayout[] = [
  {
    id: 'collage-1',
    name: '1 Photo',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [{ x: 0, y: 0, width: 100, height: 100 }],
  },
  {
    id: 'collage-2-h',
    name: '2 Photos Horizontal',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 50, height: 100 },
      { x: 50, y: 0, width: 50, height: 100 },
    ],
  },
  {
    id: 'collage-2-v',
    name: '2 Photos Vertical',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 100, height: 50 },
      { x: 0, y: 50, width: 100, height: 50 },
    ],
  },
  {
    id: 'collage-3-2-1',
    name: '3 Photos (2+1)',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 50, height: 50 },
      { x: 50, y: 0, width: 50, height: 50 },
      { x: 0, y: 50, width: 100, height: 50 },
    ],
  },
  {
    id: 'collage-3-1-2',
    name: '3 Photos (1+2)',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 100, height: 50 },
      { x: 0, y: 50, width: 50, height: 50 },
      { x: 50, y: 50, width: 50, height: 50 },
    ],
  },
  {
    id: 'collage-4',
    name: '4 Photos (2x2)',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 50, height: 50 },
      { x: 50, y: 0, width: 50, height: 50 },
      { x: 0, y: 50, width: 50, height: 50 },
      { x: 50, y: 50, width: 50, height: 50 },
    ],
  },
  {
    id: 'collage-6',
    name: '6 Photos (2x3)',
    paperType: 'collage_4x6',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 50, height: 100 / 3 },
      { x: 50, y: 0, width: 50, height: 100 / 3 },
      { x: 0, y: 100 / 3, width: 50, height: 100 / 3 },
      { x: 50, y: 100 / 3, width: 50, height: 100 / 3 },
      { x: 0, y: (100 / 3) * 2, width: 50, height: 100 / 3 },
      { x: 50, y: (100 / 3) * 2, width: 50, height: 100 / 3 },
    ],
  },
];

/** 4x6 collage landscape: same slot arrangements as portrait, different paper orientation (6" wide x 4" tall). */
export const COLLAGE_4X6_LANDSCAPE_LAYOUTS: CanvasLayout[] = COLLAGE_4X6_LAYOUTS.map((l) => ({
  ...l,
  id: `${l.id}-landscape`,
  name: `${l.name} (Landscape)`,
  orientation: 'landscape' as const,
}));

/** Hardcoded layouts for strip: vertically stacked arrangements (2" x 6"). */
export const STRIP_LAYOUTS: CanvasLayout[] = [
  {
    id: 'strip-1',
    name: '1 Photo',
    paperType: 'strip',
    orientation: 'portrait',
    slots: [{ x: 0, y: 0, width: 100, height: 100 }],
  },
  {
    id: 'strip-2',
    name: '2 Photos Stacked',
    paperType: 'strip',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 100, height: 50 },
      { x: 0, y: 50, width: 100, height: 50 },
    ],
  },
  {
    id: 'strip-3',
    name: '3 Photos Stacked',
    paperType: 'strip',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 100, height: 100 / 3 },
      { x: 0, y: 100 / 3, width: 100, height: 100 / 3 },
      { x: 0, y: (100 / 3) * 2, width: 100, height: 100 / 3 },
    ],
  },
  {
    id: 'strip-4',
    name: '4 Photos Stacked',
    paperType: 'strip',
    orientation: 'portrait',
    slots: [
      { x: 0, y: 0, width: 100, height: 25 },
      { x: 0, y: 25, width: 100, height: 25 },
      { x: 0, y: 50, width: 100, height: 25 },
      { x: 0, y: 75, width: 100, height: 25 },
    ],
  },
];

/** All layouts indexed by paper type (collage_4x6 = portrait + landscape). */
export const LAYOUTS_BY_PAPER_TYPE: Record<'collage_4x6' | 'strip', CanvasLayout[]> = {
  collage_4x6: [...COLLAGE_4X6_LAYOUTS, ...COLLAGE_4X6_LANDSCAPE_LAYOUTS],
  strip: STRIP_LAYOUTS,
};

/**
 * Get aspect ratio string for a layout (for CSS aspectRatio).
 * - collage_4x6 portrait: 2/3 (4" x 6")
 * - collage_4x6 landscape: 3/2 (6" x 4")
 * - strip: 1/3 (2" x 6")
 */
export function getAspectRatioForLayout(layout: CanvasLayout): string {
  if (layout.paperType === 'strip') return '1/3';
  if (layout.paperType === 'collage_4x6') {
    return layout.orientation === 'landscape' ? '3/2' : '2/3';
  }
  return '2/3';
}

/**
 * Get available layouts for a paper type.
 */
export function getLayoutsForPaperType(paperType: 'collage_4x6' | 'strip'): CanvasLayout[] {
  return LAYOUTS_BY_PAPER_TYPE[paperType] ?? [];
}

/**
 * Get a layout by id (searches portrait, landscape, and strip).
 */
export function getLayoutById(layoutId: string): CanvasLayout | undefined {
  return (
    COLLAGE_4X6_LAYOUTS.find((l) => l.id === layoutId) ??
    COLLAGE_4X6_LANDSCAPE_LAYOUTS.find((l) => l.id === layoutId) ??
    STRIP_LAYOUTS.find((l) => l.id === layoutId)
  );
}
